// Three.js:
import * as THREE from 'three';

// Custom:
import FVector from '@data/geometry/FVector.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { Visualization } from '../Visualizer.js';

// Camera WASD
import { CameraWasd } from '@rendering/camera/CameraWasd';

// Zustand Store
import { getGlobalState, setCurrentCamera } from '@gui/store/persistent/persistentStore';
import { CameraOrtho } from './CameraOrtho.js';
import { CameraPov } from './CameraPov.js';
import { CameraStandard } from './CameraStandard.js';
import { CameraTOP as CameraTop } from './CameraTop.js';
import CustomCamera from './CustomCamera.js';
import Grid from 'Geodata/grid.js';
import { CameraMode, NotificationType, type Camera, type FVector as IFVector } from '@visualizer/types';
import { ControllerManager } from '@gui/controller/ControllerManager';
import useNotificationStore from '@gui/store/notification.js';

// const PI = Math.PI;
const windowRatio = window.innerWidth / window.innerHeight;
const FOV = 46.4; // This mimicks a 28mm cam like in TwinMotion and Archicad // OldVersion --> THREE.MathUtils.radToDeg(PI / 3);
const CLIP_NEAR = 0.1;
const CLIP_FAR = 40000;

const orthographicDivision = 8;
const leftFrustum = window.innerWidth / -orthographicDivision;
const rightFrustum = window.innerWidth / orthographicDivision;
const topFrustum = window.innerHeight / orthographicDivision;
const bottomFrustum = window.innerHeight / -orthographicDivision;

// Signed angle between a->b around axis n (right-hand rule)
function signedAngleAroundAxis(a: THREE.Vector3, b: THREE.Vector3, n: THREE.Vector3) {
    const cross = new THREE.Vector3().crossVectors(a, b);
    const sin = cross.dot(n);
    const cos = a.dot(b);
    return Math.atan2(sin, cos);
}

export class CameraHandler {
    private readonly parentViewer: Visualization;

    private readonly orbitControls: OrbitControls;
    private readonly trackballControls: TrackballControls;

    // WASD control
    private wasd?: CameraWasd | null;

    // Cameras
    private cameraStandard: CameraStandard;
    private cameraTop: CameraTop;
    private cameraPov: CameraPov;
    private cameraOrtho: CameraOrtho;
    private orbitateEnabled = false;
    private orbitateSpeed = Math.PI / 18; // rad/sec (15°/sec)

    constructor(parentViewer: Visualization) {
        this.parentViewer = parentViewer;

        // CAMERAS

        const perspectiveCamera = new THREE.PerspectiveCamera(FOV, windowRatio, CLIP_NEAR, CLIP_FAR);
        const orthographicCamera = new THREE.OrthographicCamera(leftFrustum, rightFrustum, topFrustum, bottomFrustum, -5000, 5000);

        this.orbitControls = new OrbitControls(perspectiveCamera, parentViewer.canvas);
        this.orbitControls.enableDamping = true;
        this.orbitControls.dampingFactor = 0.085;
        this.trackballControls = new TrackballControls(perspectiveCamera, parentViewer.canvas);

        this.cameraStandard = new CameraStandard(perspectiveCamera, this.orbitControls, this.trackballControls);
        this.cameraTop = new CameraTop(orthographicCamera, this.orbitControls, this.trackballControls);
        this.cameraPov = new CameraPov(perspectiveCamera, this.orbitControls, this.trackballControls);
        this.cameraOrtho = new CameraOrtho(orthographicCamera, this.orbitControls, this.trackballControls);

        // Camera WASD
        // hardcoded default value for safety
        this.syncWasd(true);

        // Listener on movements:
        this.orbitControls.addEventListener('start', () => {
            const activeCam = getGlobalState().ambient.currentCamera.type;

            if (activeCam === CameraMode.POV) {
                const pov = this.cameraPov;

                if (pov.isLocked()) {
                    useNotificationStore
                        .getState()
                        .showNotification(
                            'Camera is in Locked mode, to release it click on the Camera icon and on Std',
                            NotificationType.Error
                        );
                }
            }
        });
    }

    // Syncs zustand in the constructor
    private syncWasd(enable: boolean) {
        if (enable && !this.wasd) {
            this.wasd = new CameraWasd(this.orbitControls);
        } else if (!enable && this.wasd) {
            this.wasd = null;
        }
    }

    public setOrbitationEnabled(enabled: boolean) {
        this.orbitateEnabled = enabled;
    }

    private applyOrbitation(deltaSec: number) {
        const cam = this.orbitControls.object as THREE.Camera;
        const target = this.orbitControls.target;

        // Offset from target & rotate around Z axis:
        const offset = cam.position.clone().sub(target);
        const angle = this.orbitateSpeed * deltaSec;
        offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);

        cam.position.copy(target).add(offset);

        // Ensure controls stay in sync:
        this.orbitControls.update();
    }

    // Called in the visualizer animation loop
    public update(deltaSec: number) {
        if (getGlobalState().ambient.wasdEnabled) {
            const keys = ControllerManager.getInstance().getCameraController().getActiveKeys();

            const speedLevel = getGlobalState().ambient.cameraWasdSpeed;
            const boostActive = keys.has('ShiftLeft') || keys.has('ShiftRight');
            const boostMultiplier = boostActive ? 2.5 : 1;
            this.wasd?.update(deltaSec, keys, speedLevel, boostMultiplier);
        }

        // Orbitation:
        if (this.orbitateEnabled) {
            this.applyOrbitation(deltaSec);
        }

        this.orbitControls.update();
        const target = this.orbitControls.target;
        this.trackballControls.target.set(target.x, target.y, target.z);
        this.trackballControls.update();
    }

    public getOrbitControls() {
        return this.orbitControls;
    }

    public getTrackballControls() {
        return this.trackballControls;
    }

    public disableInertia() {
        // OrbitControls inertia
        this.orbitControls.enableDamping = false;
        this.orbitControls.dampingFactor = 0;

        // TrackballControls “inertia”
        this.trackballControls.staticMoving = true;
        this.trackballControls.dynamicDampingFactor = 0;
    }

    /**
     *
     * @deprecated THIS IS ULTRA TEMPORARY. Its used to transfer props inside visualizer --> global state. This has to be removed!!
     */
    getProperties = (): any[] => {
        return [this.getActiveCamera(getGlobalState().ambient.currentCamera.type), this.orbitControls, this.trackballControls];
    };

    public getCustomCamera(mode: CameraMode): CustomCamera {
        switch (mode) {
            case CameraMode.STANDARD:
                return this.cameraStandard;
            case CameraMode.POV:
                return this.cameraPov;
            case CameraMode.TOP:
                return this.cameraTop;
            case CameraMode.ORTHO:
                return this.cameraOrtho;
            default:
                return this.cameraStandard;
        }
    }

    public getPovCamera(): CameraPov {
        return this.cameraPov;
    }

    public getActiveCamera(mode: CameraMode): THREE.Camera {
        const camera = this.getCustomCamera(mode);
        return camera.getCamera();
    }

    /**
     * Used to switch camera mode
     *
     * @param currentCamera Cuyrrent camera from the zustand store
     * @param nextCameraMode Usually the mode in which you are going in, or the same of the current camera
     * @param injectPositionAndTarget By default when setting new mode it will take current poosition and target from the underlying objects. If you need to force it put true
     */

    setCameraMode(currentCamera: Camera, nextCameraMode?: CameraMode, injectPositionAndTarget?: boolean) {
        // Resets grid if needed:
        if (nextCameraMode !== CameraMode.POV) this.cameraPov.deActivate();

        if (!nextCameraMode) nextCameraMode = currentCamera.type;

        const currentCustom = this.getCustomCamera(currentCamera.type);
        const fromPose = currentCustom.deActivate();

        if (nextCameraMode === CameraMode.TOP && currentCamera.type !== CameraMode.TOP) {
            this.cameraTop.setReturnFromPose(fromPose.position, fromPose.target);
        }

        if (nextCameraMode === CameraMode.POV && currentCamera.type !== CameraMode.POV) {
            this.cameraPov.setReturnPose(fromPose.position, fromPose.target);
        }

        let poseToApply: { position: IFVector; target: IFVector } = injectPositionAndTarget
            ? { position: currentCamera.position, target: currentCamera.target }
            : fromPose;

        const nextCustomCamera = this.getCustomCamera(nextCameraMode);
        if (!injectPositionAndTarget && currentCamera.type !== CameraMode.POV) {
            poseToApply = this.syncZoomBetweenCameraTypes(currentCustom.getCamera(), nextCustomCamera.getCamera(), poseToApply);
        }
        nextCustomCamera.activate(poseToApply.position, poseToApply.target);

        if (FVector.isZero(poseToApply.position) && FVector.isZero(poseToApply.target)) {
            this.resetStandard(nextCameraMode);
        }

        const nextPose = nextCustomCamera.getPositionAndTarget();
        setCurrentCamera({ type: nextCameraMode, position: nextPose.position, target: nextPose.target });

        const nextCamera = nextCustomCamera.getCamera();
        this.parentViewer.selectMode?.setCamera(nextCamera);
        this.parentViewer.postProcess.setCamera(nextCamera);
    }

    private syncZoomBetweenCameraTypes(
        currentCamera: THREE.Camera,
        nextCamera: THREE.Camera,
        pose: { position: IFVector; target: IFVector }
    ): { position: IFVector; target: IFVector } {
        const currentIsOrtho = currentCamera instanceof THREE.OrthographicCamera;
        const nextIsOrtho = nextCamera instanceof THREE.OrthographicCamera;

        // From Axo to Standard:
        if (currentIsOrtho && nextCamera instanceof THREE.PerspectiveCamera) {
            const orthoCamera = currentCamera as THREE.OrthographicCamera;
            const orthoZoom = Math.max(orthoCamera.zoom, 1e-6);
            const orthoHeight = (orthoCamera.top - orthoCamera.bottom) / orthoZoom;

            const fovRad = THREE.MathUtils.degToRad(Math.max(nextCamera.fov, 1e-6));
            const distance = orthoHeight / 2 / Math.tan(fovRad / 2);

            const target = new THREE.Vector3(pose.target.x, pose.target.y, pose.target.z);
            const position = new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
            const direction = position.clone().sub(target);

            if (direction.lengthSq() < 1e-12) {
                return pose;
            }

            direction.normalize().multiplyScalar(distance);
            const nextPosition = target.clone().add(direction);

            return {
                position: new FVector(nextPosition.x, nextPosition.y, nextPosition.z),
                target: pose.target,
            };
        }

        // From Standard to Axo:
        if (nextIsOrtho && currentCamera instanceof THREE.PerspectiveCamera) {
            const orthoCamera = nextCamera as THREE.OrthographicCamera;
            const perspectiveCamera = currentCamera as THREE.PerspectiveCamera;

            const fovRad = THREE.MathUtils.degToRad(Math.max(perspectiveCamera.fov, 1e-6));

            const target = new THREE.Vector3(pose.target.x, pose.target.y, pose.target.z);
            const position = new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
            const distance = position.distanceTo(target);
            if (distance < 1e-6) {
                return pose;
            }

            const orthoHeight = orthoCamera.top - orthoCamera.bottom;
            const nextZoom = orthoHeight / (2 * distance * Math.tan(fovRad / 2));
            orthoCamera.zoom = Math.max(nextZoom, 1e-6);
            orthoCamera.updateProjectionMatrix();
        }

        return pose;
    }

    resetStandard(currentCamera: CameraMode) {
        // Center
        const center = new FVector(Grid.siteVolumeCenter.x, Grid.siteVolumeCenter.z, -Grid.siteVolumeCenter.y);

        const phi = (Math.PI * 60) / 180;

        const res = Grid.gridRes ?? 1;
        const spanX = (Grid.gridX ?? 100) * res;
        const spanY = (Grid.gridY ?? 100) * res;
        const spanZ = (Grid.gridZ ?? 100) * res;

        const maxDim = Math.max(spanX, spanY, spanZ);

        const dist = Math.max(200, 0.9 * maxDim);

        const r = dist * Math.cos(phi);
        const z = dist * Math.sin(phi);

        // Place camera SOUTH of center (+Y) so it looks NORTH (−Y) at 'center'
        const position = center.clone().add(new FVector(0, +r, z));

        this.getCustomCamera(currentCamera).setPositionAndTarget(position, center);
    }

    resetRotation(currentCamera: CameraMode) {
        // Guard POV if it has different semantics
        if (currentCamera === CameraMode.POV) return;

        const cam = this.getCustomCamera(currentCamera);
        const { position, target } = cam.getPositionAndTarget();

        const pos3 = new THREE.Vector3(position.x, position.y, position.z);
        const tgt3 = new THREE.Vector3(target.x, target.y, target.z);

        // Use the camera's real up axis (OrbitControls expects this)
        const up = cam.getCamera().up.clone().normalize(); // likely (0,1,0)

        // "North" for your current compass math (yaw around Y):
        // yaw=0 means looking toward -Z, so NORTH should be -Z
        const north = new THREE.Vector3(0, 0, -1).projectOnPlane(up).normalize();

        // Camera offset from target
        const offset = pos3.clone().sub(tgt3);

        // Horizontal component of offset (ground plane = plane orthogonal to up)
        const horiz = offset.clone().projectOnPlane(up);
        if (horiz.lengthSq() < 1e-12) return;

        // IMPORTANT:
        // If camera is NORTH of target (offset points NORTH), it looks SOUTH.
        // To look NORTH, camera must be SOUTH of target => offset must point SOUTH.
        const desiredOffsetDir = north.clone().negate(); // SOUTH

        const ang = signedAngleAroundAxis(horiz.clone().normalize(), desiredOffsetDir, up);

        offset.applyAxisAngle(up, ang);

        const newPos = tgt3.clone().add(offset);

        cam.setPositionAndTarget(new FVector(newPos.x, newPos.y, newPos.z), new FVector(target.x, target.y, target.z));

        // Sync both controls
        this.orbitControls.target.set(tgt3.x, tgt3.y, tgt3.z);
        this.orbitControls.update();

        this.trackballControls.target.set(tgt3.x, tgt3.y, tgt3.z);
        this.trackballControls.update();
    }

    /**
     * Used to refresh the store with the current position and target of the active camera
     * @param activeMode
     * @returns
     */
    public refreshStore(activeMode: CameraMode) {
        const { position, target } = this.getCustomCamera(activeMode).getPositionAndTarget();
        setCurrentCamera({ type: activeMode, position, target });

        return { position, target };
    }

    public dispose() {
        this.orbitControls.dispose();
        this.trackballControls.dispose();
        this.wasd = null;
    }
}
